import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import SEO from '../components/SEO';
import { 
  Heart, 
  Users, 
  Target, 
  Award,
  MapPin,
  Phone,
  Mail
} from 'lucide-react';

const AboutUsPage = () => {
  const { language, t } = useLanguage();

  const content = {
    en: {
      title: 'About United Hands Karur',
      subtitle: 'Connecting Local Businesses with the Community',
      mission: 'Our Mission',
      missionText: 'To empower local businesses in Karur by providing them with a digital platform to reach more customers and grow their presence in the community.',
      vision: 'Our Vision',
      visionText: 'To become the most trusted and comprehensive business directory in Karur, helping both businesses and customers connect seamlessly.',
      values: 'Our Values',
      valuesItems: [
        { title: 'Community First', desc: 'We prioritize the needs of our local community and businesses.' },
        { title: 'Trust & Transparency', desc: 'We maintain honest and transparent relationships with all our users.' },
        { title: 'Innovation', desc: 'We continuously improve our platform to better serve our community.' },
        { title: 'Support Local', desc: 'We are committed to promoting and supporting local businesses.' }
      ],
      story: 'Our Story',
      storyText: 'United Hands Karur was created to bridge the gap between local businesses and customers in Karur. We understand the challenges small businesses face in reaching their target audience, and we\'re here to help them thrive in the digital age.',
      contact: 'Get in Touch',
      location: 'No. 90, Sri Velavan Complex, Vaiyapuri Nagar(West), Karur, TamilNadu, India - 639002',
      email: 'admin@unitedhands.cc',
      phone: '+91-7624919806'
    },
    ta: {
      title: 'எங்களை பற்றி',
      subtitle: 'உள்ளூர் வணிகங்களை சமூகத்துடன் இணைக்கிறோம்',
      mission: 'எங்கள் நோக்கம்',
      missionText: 'கரூரில் உள்ள உள்ளூர் வணிகங்களுக்கு டிஜிட்டல் தளத்தை வழங்குவதன் மூலம் அதிக வாடிக்கையாளர்களை சென்றடைய மற்றும் சமூகத்தில் அவர்களின் இருப்பை வளர்க்க உதவுதல்.',
      vision: 'எங்கள் பார்வை',
      visionText: 'கரூரில் மிகவும் நம்பகமான மற்றும் விரிவான வணிக அடைவுகோலாக மாறுவது, வணிகங்கள் மற்றும் வாடிக்கையாளர்கள் தடையின்றி இணைக உதவுதல்.',
      values: 'எங்கள் மதிப்புகள்',
      valuesItems: [
        { title: 'சமூகம் முதலில்', desc: 'எங்கள் உள்ளூர் சமூகம் மற்றும் வணிகங்களின் தேவைகளுக்கு முன்னுரிமை அளிக்கிறோம்.' },
        { title: 'நம்பிக்கை & வெளிப்படைத்தன்மை', desc: 'எங்கள் அனைத்து பயனர்களுடனும் நேர்மையான மற்றும் வெளிப்படையான உறவுகளை பராமரிக்கிறோம்.' },
        { title: 'புதுமை', desc: 'எங்கள் சமூகத்திற்கு சிறப்பாக சேவை செய்ய தளத்தை தொடர்ந்து மேம்படுத்துகிறோம்.' },
        { title: 'உள்ளூரை ஆதரிக்கவும்', desc: 'உள்ளூர் வணிகங்களை ஊக்குவிக்கவும் ஆதரிக்கவும் நாங்கள் உறுதிபூண்டுள்ளோம்.' }
      ],
      story: 'எங்கள் கதை',
      storyText: 'கரூரில் உள்ளூர் வணிகங்களுக்கும் வாடிக்கையாளர்களுக்கும் இடையிலான இடைவெளியை நிரப்ப யுனைடெட் ஹேண்ட்ஸ் கரூர் உருவாக்கப்பட்டது. சிறு வணிகங்கள் தங்கள் இலக்கு பார்வையாளர்களை சென்றடைவதில் சந்திக்கும் சவால்களை நாங்கள் புரிந்துகொள்கிறோம், மேலும் டிஜிட்டல் யுகத்தில் அவர்கள் செழிக்க நாங்கள் இங்கே உள்ளோம்.',
      contact: 'தொடர்பு கொள்ளுங்கள்',
      location: 'எண். 90, ஸ்ரீ வேலவன் காம்ப்ளக்ஸ், வையபுரி நகர்(மேற்கு), கரூர், தமிழ்நாடு, இந்தியா - 639002',
      email: 'admin@unitedhands.cc',
      phone: '+91-7624919806'
    }
  };

  const c = content[language];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About United Hands Karur Shop",
    "description": "Learn about our mission to connect local businesses with customers in Karur",
    "url": "https://yourwebsite.com/about"
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50">
      <SEO
        title="About Us | United Hands Karur Shop - Supporting Local Businesses"
        description="United Hands Karur Shop is your trusted local business directory connecting customers with verified businesses in Karur, Tamil Nadu. Learn about our mission to support local businesses."
        keywords="about United Hands, Karur business directory, our mission, local business support, community platform, Karur local services"
        structuredData={structuredData}
      />
      
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 font-['Manrope']">
              {c.title}
            </h1>
            <p className="text-xl md:text-2xl text-blue-100">
              {c.subtitle}
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 py-12">
        {/* Mission & Vision */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          <Card className="shadow-xl border-2 border-white/50 hover:shadow-2xl transition-shadow">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Target className="h-6 w-6 text-blue-600" />
                {c.mission}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-slate-700 leading-relaxed text-lg">
                {c.missionText}
              </p>
            </CardContent>
          </Card>

          <Card className="shadow-xl border-2 border-white/50 hover:shadow-2xl transition-shadow">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 border-b">
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Award className="h-6 w-6 text-purple-600" />
                {c.vision}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-slate-700 leading-relaxed text-lg">
                {c.visionText}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Our Values */}
        <Card className="shadow-xl border-2 border-white/50 mb-12">
          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Heart className="h-6 w-6 text-green-600" />
              {c.values}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {c.valuesItems.map((value, index) => (
                <div 
                  key={index} 
                  className="p-5 rounded-lg bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all"
                >
                  <h3 className="font-bold text-lg text-slate-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {value.desc}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Our Story */}
        <Card className="shadow-xl border-2 border-white/50 mb-12">
          <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50 border-b">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Users className="h-6 w-6 text-orange-600" />
              {c.story}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-slate-700 leading-relaxed text-lg">
              {c.storyText}
            </p>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="shadow-xl border-2 border-white/50 max-w-2xl mx-auto">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b">
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Mail className="h-6 w-6 text-blue-600" />
              {c.contact}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start gap-4 p-4 rounded-lg hover:bg-slate-50 transition-colors">
              <div className="bg-blue-100 p-2 rounded-lg">
                <MapPin className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 mb-1">
                  {language === 'ta' ? 'இடம்' : 'Location'}
                </p>
                <p className="text-slate-700">{c.location}</p>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-lg hover:bg-slate-50 transition-colors">
              <div className="bg-green-100 p-2 rounded-lg">
                <Mail className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 mb-1">
                  {language === 'ta' ? 'மின்னஞ்சல்' : 'Email'}
                </p>
                <a href={`mailto:${c.email}`} className="text-blue-600 hover:underline">
                  {c.email}
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 rounded-lg hover:bg-slate-50 transition-colors">
              <div className="bg-purple-100 p-2 rounded-lg">
                <Phone className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900 mb-1">
                  {language === 'ta' ? 'தொலைபேசி' : 'Phone'}
                </p>
                <a href={`tel:${c.phone}`} className="text-blue-600 hover:underline">
                  {c.phone}
                </a>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AboutUsPage;
